// `/` — marketing landing for unauthenticated visitors. Authenticated users
// are bounced to /screen by the AuthGate.
//
// Design: same dark cobalt-charcoal canvas as the terminal. Single-page,
// terminal-aesthetic — no marketing-website tropes (no hero gradient, no
// stock photos, no rotating testimonials). Operator-grade copy.

import { Pill } from '@/primitives';
import { Link, createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/')({
  component: MarketingLanding,
});

function MarketingLanding() {
  return (
    <div
      className="eid"
      style={{
        height: '100vh',
        width: '100vw',
        background: 'var(--bg-base)',
        color: 'var(--fg-1)',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="class-stripe pub" />

      {/* Top bar */}
      <header
        style={{
          height: 48,
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          borderBottom: '1px solid var(--bd)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mark size={18} />
          <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: 1.6 }}>EIDOLLON</span>
          <span className="meta mono">v0.1.0</span>
        </div>
        <div style={{ flex: 1 }} />
        <Link to="/login" className="btn pri" style={{ height: 28 }}>
          SIGN IN
        </Link>
      </header>

      <main>
        {/* Hero — text-only, mono-cap density */}
        <section
          style={{ padding: '64px 24px 32px', maxWidth: 1100, margin: '0 auto', width: '100%' }}
        >
          <div className="lbl" style={{ color: 'var(--primary)' }}>
            OPERATOR TERMINAL · GOVERNMENT CONTRACTING INTELLIGENCE
          </div>
          <h1
            style={{
              fontSize: 48,
              fontWeight: 600,
              lineHeight: 1.1,
              letterSpacing: -0.5,
              marginTop: 12,
              marginBottom: 18,
              maxWidth: 760,
            }}
          >
            One workspace for every U.S., Canadian, and allied solicitation, award, and contract.
          </h1>
          <p
            className="prose"
            style={{
              fontSize: 16,
              lineHeight: 1.55,
              color: 'var(--fg-2)',
              maxWidth: 680,
              marginBottom: 28,
            }}
          >
            Eidollon is a Bloomberg-density operator terminal for federal-sector capture, bid
            execution, and competitive intelligence. Every notice across SAM.gov, FPDS, USAspending,
            CanadaBuys, ProcureData, TED EU, and allied portals — searched, filtered, scored,
            attributed. Built for primes, multi-tenant from day one, secured to a SOC 2 / NIST
            800-171 baseline before the first feature shipped.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Link to="/login" className="btn pri" style={{ height: 32, padding: '0 16px' }}>
              SIGN IN TO TERMINAL
            </Link>
            <span className="meta mono">
              No public signup · enterprise SSO via SAML/OIDC (Phase 1)
            </span>
          </div>
        </section>

        {/* Live numbers — pulled from the live API would be ideal, but the
          anonymous user has no token. Use static numbers that match the
          actual ingested corpus today. */}
        <section
          style={{
            padding: '32px 24px',
            borderTop: '1px solid var(--bd)',
            borderBottom: '1px solid var(--bd)',
            background: 'var(--bg-pane)',
          }}
        >
          <div
            style={{
              maxWidth: 1100,
              margin: '0 auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 24,
            }}
          >
            <Stat n="64,167" k="RECORDS INGESTED" />
            <Stat n="7,455" k="TENDERS" />
            <Stat n="19,198" k="CONTRACTS" />
            <Stat n="15,002" k="AWARDS" />
          </div>
          <div className="meta mono" style={{ marginTop: 16, textAlign: 'center' }}>
            ProcureData (Canada) · SAM.gov + FPDS + USAspending coming Phase 1
          </div>
        </section>

        {/* Pillars */}
        <section style={{ padding: '48px 24px', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
          <div className="lbl" style={{ marginBottom: 18 }}>
            WHAT IS THE TERMINAL
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            <Pillar
              tag="01 · DISCOVERY"
              title="Find every opportunity that matches your shop"
              body="Filter, search, save, and alert on solicitations across every public portal. Fuzzy-match agencies and vendors automatically. Cross-reference against your past awards and incumbent map."
            />
            <Pillar
              tag="02 · INTELLIGENCE"
              title="Ground every decision in signal"
              body="Live overlays for sanctions (OFAC, EU, UN), conflict events (ACLED), supply-chain disruptions, agency budget posture, and incumbent OCI risk. Map-first canvas with MIL-STD-2525 entity glyphs."
            />
            <Pillar
              tag="03 · EXECUTION"
              title="Draft, comply, ship"
              body="Three-pane bid workspace pulling §L straight from the RFP, agent-suggested narrative, compliance matrix mapped to NIST 800-171 / 800-53. Audit trail on every decision, immutable, hash-chained."
            />
          </div>
        </section>

        {/* Security strip — operator-grade, not a feature list */}
        <section
          style={{
            padding: '32px 24px',
            borderTop: '1px solid var(--bd)',
            background: 'var(--bg-pane)',
          }}
        >
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div className="lbl" style={{ marginBottom: 14 }}>
              SECURITY POSTURE — DAY ONE
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 14,
                fontSize: 12,
              }}
            >
              <SecLine on>
                Argon2id passwords · TOTP-mandatory MFA · rotating refresh tokens with reuse
                detection
              </SecLine>
              <SecLine on>
                Postgres row-level security · per-tenant Vault Transit keys · pgaudit on every write
              </SecLine>
              <SecLine on>
                JWT with IP / user-agent binding · 15-minute access tokens · HTTPS-only refresh
                cookies
              </SecLine>
              <SecLine on>Append-only audit ledger · hash-chained · tenant-scoped</SecLine>
              <SecLine on>Zero secrets in repo · Vault for runtime · GitHub OIDC for CI</SecLine>
              <SecLine on>
                SOC 2 Type I posture in 90 days · Type II observation window opens at first paying
                customer
              </SecLine>
            </div>
          </div>
        </section>

        {/* Closing */}
        <section
          style={{ padding: '48px 24px 64px', maxWidth: 1100, margin: '0 auto', width: '100%' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div className="lbl" style={{ color: 'var(--primary)' }}>
                READY TO OPERATE
              </div>
              <h2 style={{ fontSize: 26, marginTop: 6, marginBottom: 8, fontWeight: 600 }}>
                Sign in to access the terminal.
              </h2>
              <p className="prose" style={{ color: 'var(--fg-2)', maxWidth: 540 }}>
                Eidollon is enterprise-only. To request access, contact your tenant administrator.
              </p>
            </div>
            <Link to="/login" className="btn pri" style={{ height: 32, padding: '0 18px' }}>
              SIGN IN
            </Link>
          </div>
        </section>
      </main>

      <footer
        style={{
          padding: '10px 24px',
          borderTop: '1px solid var(--bd)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-pane)',
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          flexShrink: 0,
        }}
      >
        <span>© 2026 EIDOLLON · OPERATOR TERMINAL</span>
        <div style={{ display: 'flex', gap: 14 }}>
          <Pill tone="green" dot={false}>
            SOC 2 IN PROGRESS
          </Pill>
          <span>SECURITY · CONTACT@EIDOLLON.COM</span>
        </div>
      </footer>
    </div>
  );
}

function Stat({ n, k }: { n: string; k: string }) {
  return (
    <div>
      <div className="metric" style={{ fontSize: 32 }}>
        {n}
      </div>
      <div className="lbl" style={{ marginTop: 4 }}>
        {k}
      </div>
    </div>
  );
}

function Pillar({ tag, title, body }: { tag: string; title: string; body: string }) {
  return (
    <div style={{ borderLeft: '2px solid var(--primary)', paddingLeft: 14 }}>
      <div className="lbl" style={{ color: 'var(--primary)' }}>
        {tag}
      </div>
      <div className="title-l" style={{ marginTop: 6, marginBottom: 8, lineHeight: 1.3 }}>
        {title}
      </div>
      <p className="prose" style={{ color: 'var(--fg-2)', fontSize: 12, lineHeight: 1.6 }}>
        {body}
      </p>
    </div>
  );
}

function SecLine({ children, on }: { children: React.ReactNode; on?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.55 }}>
      <span
        style={{
          flexShrink: 0,
          marginTop: 6,
          width: 8,
          height: 8,
          background: on ? 'var(--green)' : 'var(--fg-4)',
        }}
      />
      <span className="dim">{children}</span>
    </div>
  );
}

function Mark({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="var(--primary)"
      strokeWidth="1.4"
      aria-hidden
    >
      <path d="M1 8L8 1L15 8L8 15L1 8Z" />
      <path d="M5 8L8 5L11 8L8 11L5 8Z" />
    </svg>
  );
}
